import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, Building2, MapPin, Calendar, Clock, Edit2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { formatDate } from '../../crmConstants';

const API = process.env.REACT_APP_BACKEND_URL;

const EDIT_FIELDS = [
  { field: 'name', label: 'Name', type: 'text' },
  { field: 'email', label: 'Email', type: 'email' },
  { field: 'phone', label: 'Phone', type: 'tel' },
  { field: 'company', label: 'Company', type: 'text' },
  { field: 'state', label: 'State', type: 'text' },
  { field: 'energy_type', label: 'Energy Type', type: 'text' },
  { field: 'annual_usage', label: 'Annual Usage', type: 'text' },
  { field: 'current_rate', label: 'Current Rate', type: 'text' },
  { field: 'deal_value', label: 'Deal Value ($)', type: 'number' },
];

const sectionStyle = { background: '#FAFBFC', borderRadius: 8, padding: '1rem', border: '1px solid #E5E7EB' };
const sectionTitleStyle = { fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '0.75rem' };

const CommissionSection = ({ deal, dealId, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [companyPct, setCompanyPct] = useState(deal.company_commission_percent || 10);
  const [repPct, setRepPct] = useState(deal.sales_rep_commission_percent || 30);

  const dealValue = deal.deal_value || deal.annual_savings || 0;
  const companyAmt = dealValue * ((deal.company_commission_percent || 10) / 100);
  const repAmt = companyAmt * ((deal.sales_rep_commission_percent || 30) / 100);
  const fmt = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const save = async () => {
    try {
      await axios.put(`${API}/api/admin/deals/${dealId}/commission`, {
        company_commission_percent: parseFloat(companyPct),
        sales_rep_commission_percent: parseFloat(repPct),
      }, { withCredentials: true });
      setEditing(false);
      onRefresh();
    } catch (err) {
      console.error('Failed to update commission:', err);
      alert(err.response?.data?.detail || 'Failed to update commission');
    }
  };

  return (
    <div style={{ background: '#F5F3FF', borderRadius: 8, padding: '1rem', border: '1px solid #DDD6FE' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h4 style={{ fontSize: '0.7rem', fontWeight: 600, color: '#7C3AED', textTransform: 'uppercase', margin: 0 }}>
          Commission (Admin Only)
        </h4>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            data-testid="edit-commission-btn"
            style={{
              background: 'none', border: 'none', cursor: 'pointer', color: '#7C3AED',
              display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem'
            }}
          >
            <Edit2 size={12} /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#6B7280', width: 80 }}>Company:</span>
            <Input
              type="number" min="0" max="100" step="0.1"
              value={companyPct}
              onChange={(e) => setCompanyPct(e.target.value)}
              style={{ width: 70, background: '#fff' }}
              data-testid="commission-input"
            />
            <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>%</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#6B7280', width: 80 }}>Sales Rep:</span>
            <Input
              type="number" min="0" max="100" step="0.1"
              value={repPct}
              onChange={(e) => setRepPct(e.target.value)}
              style={{ width: 70, background: '#fff' }}
              data-testid="sales-rep-commission-input"
            />
            <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>% of company</span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
            <Button size="sm" onClick={save} style={{ background: '#7C3AED', color: '#fff' }} data-testid="save-commission-btn">
              Save
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => {
                setEditing(false);
                setCompanyPct(deal.company_commission_percent || 10);
                setRepPct(deal.sales_rep_commission_percent || 30);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div>
            <div style={{ fontSize: '0.65rem', fontWeight: 500, color: '#9CA3AF', textTransform: 'uppercase' }}>Company Rate</div>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#7C3AED' }}>{deal.company_commission_percent || 10}%</div>
          </div>
          <div>
            <div style={{ fontSize: '0.65rem', fontWeight: 500, color: '#9CA3AF', textTransform: 'uppercase' }}>Company Amount</div>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#7C3AED' }}>${fmt(companyAmt)}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.65rem', fontWeight: 500, color: '#9CA3AF', textTransform: 'uppercase' }}>Rep Rate</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#059669' }}>{deal.sales_rep_commission_percent || 30}% of company</div>
          </div>
          <div>
            <div style={{ fontSize: '0.65rem', fontWeight: 500, color: '#9CA3AF', textTransform: 'uppercase' }}>Rep Payout</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#059669' }}>${fmt(repAmt)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export const DealOverviewTab = ({ deal, dealId, team, userRole, editing, editData, setEditData, onSaveEdit, onAssign, onRefresh }) => {
  if (editing) {
    return (
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        {EDIT_FIELDS.map(f => (
          <div key={f.field}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
              {f.label}
            </label>
            <Input
              type={f.type}
              value={editData[f.field] || ''}
              onChange={e => setEditData({ ...editData, [f.field]: e.target.value })}
              data-testid={`deal-edit-${f.field}`}
              style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)', fontSize: '0.875rem' }}
            />
          </div>
        ))}
        <Button onClick={onSaveEdit} className="btn-primary" data-testid="deal-save-edit-btn">Save Changes</Button>
      </div>
    );
  }

  const contactItems = [
    { icon: Mail, value: deal.email },
    { icon: Phone, value: deal.phone },
    { icon: Building2, value: deal.company || deal.business_name },
    { icon: MapPin, value: deal.state },
  ].filter(item => item.value);

  const detailItems = [
    { label: 'Energy Type', value: deal.energy_type },
    { label: 'Annual Usage', value: deal.annual_usage },
    { label: 'Current Rate', value: deal.current_rate ? `$${deal.current_rate}` : null },
    { label: 'Offer Rate', value: deal.offer_rate ? `$${deal.offer_rate}` : null },
    { label: 'Savings %', value: deal.savings_percent ? `${deal.savings_percent}%` : null },
    { label: 'Annual Savings', value: deal.annual_savings ? `$${deal.annual_savings}` : null },
    { label: 'Deal Value', value: deal.deal_value ? `$${parseFloat(deal.deal_value).toLocaleString()}` : null },
    { label: 'Source', value: deal.source },
  ];

  return (
    <>
      <div style={sectionStyle}>
        <h4 style={sectionTitleStyle}>Contact Information</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {contactItems.map((item) => (
            <div key={item.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.813rem', color: '#4B5563' }}>
              <item.icon size={14} style={{ color: '#9CA3AF' }} />
              {item.value}
            </div>
          ))}
        </div>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionTitleStyle}>Deal Details</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {detailItems.map((item) => (
            <div key={item.label}>
              <div style={{ fontSize: '0.65rem', fontWeight: 500, color: '#9CA3AF', textTransform: 'uppercase' }}>{item.label}</div>
              <div style={{ fontSize: '0.813rem', fontWeight: 500, color: 'var(--navy)' }}>{item.value || '—'}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={sectionStyle}>
        <h4 style={{ ...sectionTitleStyle, marginBottom: '0.5rem' }}>Assigned To</h4>
        <select
          value={deal.assigned_to || ''}
          onChange={(e) => onAssign(e.target.value || null)}
          data-testid="deal-assignee-select"
          style={{
            width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #E5E7EB',
            fontSize: '0.875rem', color: 'var(--navy)', background: '#fff',
          }}
        >
          <option value="">Unassigned</option>
          {team.map(member => (
            <option key={member.id} value={member.id}>{member.name}</option>
          ))}
        </select>
      </div>

      {userRole === 'admin' && (
        <CommissionSection deal={deal} dealId={dealId} onRefresh={onRefresh} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
        <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
          <Calendar size={12} style={{ marginRight: 4, display: 'inline' }} />
          Created: {formatDate(deal.created_at || deal.timestamp, true)}
        </div>
        {deal.updated_at && (
          <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
            <Clock size={12} style={{ marginRight: 4, display: 'inline' }} />
            Updated: {formatDate(deal.updated_at, true)}
          </div>
        )}
      </div>
    </>
  );
};
